import {
	ClientEntityType,
	JobEntityType,
	JobItemEntityType,
	PatientEntityType,
	SupabaseTableEnum,
} from "@/lib/supabase/supabaseTypes";
import { prepareDocumentExportData } from "../../pages/debitoren/utils";
import {
	PdfTemplateType,
	XmlTemplateType,
} from "../../pdf-templates/document-template.types";
import { Logger, ErrorCategoryTag } from "@/lib/logger/Logger";
import { showNotification } from "../../store/Central/selectors";
import {
	GsStatusEnum,
	KvStatusEnum,
	LsStatusEnum,
} from "../actions/actions-hooks-types";
import { calculateJobTotalsLegacy } from "../../lib/utils/calculate";
import { useCentralStore } from "../../store/Central";
import { useJobStore } from "../../store/Jobs";
import { useExportActions } from "../actions/useExportActions";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "@/lib/supabase";
import { StorageBucketsEnum } from "../../types/enums";
import { JobStatusEnum } from "@/lib/types/job";
import { usePatientStore } from "../../store/Patient";
import { getBankAccount } from "../actions/get-bank-account/get-bank-account";
import { useAccountingStore } from "../../store/Accounting";
import { InvoiceType } from "../../types/database-enums/invoices-enums";
import { JobDocumentWithFiles } from "../../store/Jobs/job-documents.store";
import {
	documentIsDocument,
	documentIsInvoice,
	documentIsXml,
	getFileNameForDocument,
	getFileNameForXml,
} from "./export-document-utils";
import { LazyPatient } from "../../store/Patient/patients.store";
import { createFileMetadata } from "@/lib/utils/file-utils";
import { openSupportChat } from "../../lib/utils/support-chat";
import type { ExportXmlResult } from "../useExportXml/useExportXml";
import {
	PreflightProblem,
	settingsTargetFor,
} from "../useExportXml/sumex-preflight";
import { runInvoicePreflight } from "./invoice-preflight";
import { NO_PATIENT_PROBLEM } from "../../store/Jobs/utils";
import {
	dispatchPrint,
	isAutoPrintEnabled,
} from "../../lib/utils/print-dispatch";
import { resolveRegionKind } from "../../lib/utils/region-utils";

const shouldMarkDocumentCompleted = (status?: JobStatusEnum | null) =>
	status === JobStatusEnum.IN_PROGRESS;

export type HandleExportDocumentResult =
	| {
			success: true;
			fileId: string;
			blob?: Blob | null;
			fileName?: string;
	  }
	| {
			success: false;
			error: string;
	  };

export const useHandleExportDocument = () => {
	const {
		paymentSlipDataFor,
		exportRechnung,
		exportBarzahlungsquittung,
		exportKartenzahlungsquittung,
		exportKostenvoranschlag,
		exportLieferschein,
		exportAuftragsblatt,
		exportGutschrift,
		exportXmlKostenvoranschlag,
		exportXmlLieferschein,
	} = useExportActions();

	const { patientsLookup } = usePatientStore((state) => ({
		patientsLookup: state.patientsLookup,
	}));

	const {
		jobState,
		updateJobDocumentStatus,
		fetchJobDocument,
		setFieldProblems,
	} = useJobStore((state) => ({
		jobState: state.job,
		updateJobDocumentStatus: state.updateJobDocumentStatus,
		fetchJobDocument: state.fetchJobDocument,
		setFieldProblems: state.setFieldProblems,
	}));

	const { organization, clientState, bankAccountsLookup, activatedRegions } =
		useCentralStore((state) => ({
			organization: state.organization,
			clientState: state.client,
			bankAccountsLookup: state.bankAccountsLookup,
			activatedRegions: state.activatedRegions,
		}));

	const { createInvoiceSingle } = useAccountingStore((state) => ({
		createInvoiceSingle: state.createInvoiceSingle,
	}));

	const navigate = useNavigate();

	const [jobDocumentLoading, setJobDocumentLoading] = useState<number | null>(
		null
	);

	/**
	 * Marks every job-page field a Sumex problem points to and focuses the first.
	 * Fields on other screens (client, organization) can only be named in the toast.
	 */
	const applySumexProblems = (
		jobId: number | null,
		problems: PreflightProblem[]
	) =>
		setFieldProblems(
			jobId,
			"sumex-export",
			problems.flatMap((problem) =>
				problem.jobField
					? [{ fieldId: problem.jobField, message: problem.reason }]
					: []
			)
		);

	const handleExportDocument = async (
		templateType: PdfTemplateType | XmlTemplateType,
		jobItems: JobItemEntityType[],
		jobDocument: JobDocumentWithFiles,
		akontoToUse: number = 0,
		noPaymentSlip?: boolean,
		jobOverwrite?: JobEntityType,
		clientOverwrite?: ClientEntityType
	): Promise<HandleExportDocumentResult> => {
		const client = clientOverwrite ?? clientState;
		const job = jobOverwrite ?? jobState;

		if (!job) {
			showNotification({
				message:
					"Auftrag nicht gefunden. Bitte versuchen Sie es erneut.",
				type: "error",
			});
			return {
				success: false,
				error: "Auftrag nicht gefunden. Bitte versuchen Sie es erneut.",
			};
		}
		if (!client) {
			showNotification({
				message: "Kunde nicht gefunden. Bitte versuchen Sie es erneut.",
				type: "error",
			});
			return {
				success: false,
				error: "Kunde nicht gefunden. Bitte versuchen Sie es erneut.",
			};
		}
		if (!organization) {
			showNotification({
				message:
					"Organisation nicht gefunden. Bitte versuchen Sie es erneut.",
				type: "error",
			});
			return {
				success: false,
				error: "Organisation nicht gefunden. Bitte versuchen Sie es erneut.",
			};
		}

		setJobDocumentLoading(jobDocument.id);

		// Use the new utility function to prepare export data with akonto
		const prepareResult = await prepareDocumentExportData({
			job,
			client,
			organization,
			jobDocument,
			jobItems,
			templateType: templateType as PdfTemplateType,
			bankAccountsLookup,
			akontoAmount: akontoToUse,
		});

		if (!prepareResult.success) {
			setJobDocumentLoading(null);
			if (prepareResult.problems) {
				setFieldProblems(
					job.id,
					"invoice-preflight",
					prepareResult.problems
				);
			}
			showNotification({
				message:
					prepareResult.error ||
					"Fehler bei der Vorbereitung der Exportdaten",
				type: "error",
			});
			return {
				success: false,
				error:
					prepareResult.error ||
					"Fehler bei der Vorbereitung der Exportdaten",
			};
		}

		const { documentProps, bankAccount, completeJobInformation } =
			prepareResult.data!;

		let fileId = "";
		let resultBlob: Blob | null = null;
		let resultFileName: string | undefined;
		if (documentIsInvoice(templateType)) {
			const invoiceValue = calculateJobTotalsLegacy({
				job,
				jobDocument,
				jobItems,
				organization,
				client,
			}).total.value;

			let fileNamePrefix = "Rechnung-";
			let invoiceType = InvoiceType.RECHNUNG;
			if (templateType === PdfTemplateType.BARZAHLUNGSQUITTUNG) {
				fileNamePrefix = "Barzahlungsquittung-";
				invoiceType = InvoiceType.BARRECHNUNG;
			} else if (
				templateType === PdfTemplateType.KARTENZAHLUNGSQUITTUNG
			) {
				fileNamePrefix = "Kartenzahlungsquittung-";
				invoiceType = InvoiceType.KARTENZAHLUNGSRECHNUNG;
			} else if (templateType === PdfTemplateType.AKONTORECHNUNG) {
				showNotification({
					message: "Akontorechnung noch nicht implementiert",
					type: "error",
				});
			}

			// Checked before createInvoiceSingle: a failure afterwards would leave a used
			// invoice number without a document.
			const preflight = runInvoicePreflight({
				documentProps,
				organization,
				bankAccountsLookup,
				validateDebtor: templateType === PdfTemplateType.RECHNUNG,
				validateCreditor:
					templateType === PdfTemplateType.RECHNUNG &&
					!noPaymentSlip &&
					invoiceValue - akontoToUse > 0,
			});
			setFieldProblems(job.id, "invoice-preflight", preflight.problems);
			if (preflight.messages.length > 0) {
				setJobDocumentLoading(null);
				const { settingsTarget } = preflight;
				showNotification({
					message: "Die Rechnung kann nicht erstellt werden.",
					description: preflight.messages.join("\n"),
					type: "error",
					duration: 10000,
					action: settingsTarget && {
						label: settingsTarget.label,
						onClick: () => navigate(settingsTarget.path),
						testId: settingsTarget.testId,
					},
				});
				return {
					success: false,
					error: preflight.messages.join("\n"),
				};
			}

			const {
				success: createInvoiceSuccess,
				data: createInvoiceData,
				error: createInvoiceError,
			} = await createInvoiceSingle({
				fileNamePrefix,
				invoiceValue,
				bankAccount,
				invoiceType,
				pdfTemplateType: templateType as PdfTemplateType,
				client,
				organization,
				jobDocument,
				job,
				akontoAmount: akontoToUse,
			});

			if (!createInvoiceSuccess || !createInvoiceData) {
				setJobDocumentLoading(null);
				showNotification({
					message:
						"Rechnung konnte nicht erstellt werden. Bitte versuchen Sie es erneut.",
					type: "error",
				});
				Logger.error(createInvoiceError, {}, "Error creating invoice");
				return {
					success: false,
					error: "Rechnung konnte nicht erstellt werden. Bitte versuchen Sie es erneut.",
				};
			}

			const { invoiceNumber, filePath, akontoUsed } = createInvoiceData;

			// Calculate remaining amount after akonto
			const remainingAmount = invoiceValue - akontoUsed;
			const shouldGeneratePaymentSlip = remainingAmount > 0;

			// Generate payment slip data with remaining amount after akonto
			const paymentSlipData = paymentSlipDataFor(
				documentProps,
				invoiceNumber,
				remainingAmount.toFixed(2)
			);
			if (!paymentSlipData) {
				setJobDocumentLoading(null);
				return {
					success: false,
					error: "Zahlungsinformationen konnten nicht erstellt werden. Bitte versuchen Sie es erneut.",
				};
			}

			let success;
			let exportBlob: Blob | null = null;
			if (templateType === PdfTemplateType.RECHNUNG) {
				const { success: s, blob } = await exportRechnung(
					documentProps,
					paymentSlipData,
					job.patient_id
						? (documentProps.patient as PatientEntityType)
						: undefined,
					filePath,
					noPaymentSlip || !shouldGeneratePaymentSlip
				);
				success = s;
				exportBlob = blob ?? null;
			} else if (templateType === PdfTemplateType.BARZAHLUNGSQUITTUNG) {
				const { success: s, blob } = await exportBarzahlungsquittung(
					documentProps,
					paymentSlipData,
					filePath
				);
				success = s;
				exportBlob = blob ?? null;
			} else if (
				templateType === PdfTemplateType.KARTENZAHLUNGSQUITTUNG
			) {
				const { success: s, blob } = await exportKartenzahlungsquittung(
					documentProps,
					paymentSlipData,
					filePath
				);
				success = s;
				exportBlob = blob ?? null;
			} else if (templateType === PdfTemplateType.AKONTORECHNUNG) {
				// TODO
				success = false;
			} else {
				success = false;
				Logger.error("Unknown invoice type");
			}

			if (!success) {
				setJobDocumentLoading(null);
				showNotification({
					message:
						"Fehler beim Erstellen des PDF-Dokuments. Bitte versuchen Sie es erneut.",
					type: "error",
				});
				return {
					success: false,
					error: "Fehler beim Erstellen des PDF-Dokuments. Bitte versuchen Sie es erneut.",
				};
			}
			resultBlob = exportBlob;
		} else if (documentIsDocument(templateType)) {
			const fileName = getFileNameForDocument({
				templateType,
				jobDocument,
				job,
				client,
				patientsLookup: patientsLookup as Record<string, LazyPatient>,
				region: resolveRegionKind(
					client,
					organization,
					activatedRegions
				),
			});
			const pathName = uuidv4() + "/" + fileName;

			let exportError = null;
			let exportBlob: Blob | null = null;
			if (templateType === PdfTemplateType.KOSTENVORANSCHLAG) {
				const { error, blob } = await exportKostenvoranschlag(
					documentProps,
					pathName,
					job.patient_id
						? (documentProps.patient as PatientEntityType)
						: undefined
				);
				exportError = error;
				exportBlob = blob ?? null;
				if (
					!exportError &&
					jobDocument.status !== JobStatusEnum.COMPLETED
				) {
					updateJobDocumentStatus(
						jobDocument.id,
						KvStatusEnum.COMPLETED,
						calculateJobTotalsLegacy({
							job,
							jobDocument,
							jobItems,
							organization,
							client,
						}).total.value,
						null
					);
				}
			} else if (templateType === PdfTemplateType.LIEFERSCHEIN) {
				const { error, blob } = await exportLieferschein(
					documentProps,
					pathName,
					job.patient_id
						? (documentProps.patient as PatientEntityType)
						: undefined
				);
				exportError = error;
				exportBlob = blob ?? null;

				const shouldCompleteDocument = shouldMarkDocumentCompleted(
					jobDocument.status as JobStatusEnum
				);

				if (!exportError && shouldCompleteDocument) {
					updateJobDocumentStatus(
						jobDocument.id,
						LsStatusEnum.COMPLETED,
						calculateJobTotalsLegacy({
							job,
							jobDocument,
							jobItems,
							organization,
							client,
						}).total.value,
						null
					);
				}
			} else if (templateType === PdfTemplateType.AUFTRAGSBLATT) {
				const qrCodeUrl = import.meta.env.VITE_QR_CODE_URL
					? import.meta.env.VITE_QR_CODE_URL +
						`/job/${documentProps.job.id}`
					: window.location.origin + `/job/${documentProps.job.id}`;
				const { error, blob } = await exportAuftragsblatt(
					{
						job: documentProps.job,
						client: documentProps.client,
						organization: documentProps.organization,
						patient: documentProps.patient,
						practitioner: documentProps.practitioner,
						guarantor: documentProps.guarantor,
						qrCodeUrl,
						jobDocument: documentProps.jobDocument,
						jobItems: documentProps.jobItems,
					} as any,
					pathName
				);
				exportError = error;
				exportBlob = blob ?? null;
			} else if (templateType === PdfTemplateType.GUTSCHRIFT) {
				const { error, blob } = await exportGutschrift(
					documentProps,
					pathName,
					job.patient_id
						? (documentProps.patient as PatientEntityType)
						: undefined
				);
				exportError = error;
				exportBlob = blob ?? null;

				if (!exportError) {
					updateJobDocumentStatus(
						jobDocument.id,
						GsStatusEnum.COMPLETED,
						calculateJobTotalsLegacy({
							job,
							jobDocument,
							jobItems,
							organization,
							client,
						}).total.value * -1, // because it's a credit note
						null
					);
				}
			}

			if (exportError) {
				setJobDocumentLoading(null);
				showNotification({
					message:
						"Export-Fehler beim Erstellen des PDF-Dokuments. Bitte versuchen Sie es erneut.",
					type: "error",
					description: exportError,
				});
				Logger.error(
					"Error creating Lieferschein 1",
					{},
					{ exportError }
				);
				return {
					success: false,
					error:
						exportError ||
						"Export-Fehler beim Erstellen des PDF-Dokuments.",
				};
			}

			const organizationId = organization.id;
			if (!organizationId) {
				showNotification({
					message:
						"Organisation ID nicht gefunden. Bitte versuchen Sie es erneut.",
					type: "error",
				});
				Logger.error("Organization ID not found", {}, organization);
				return {
					success: false,
					error: "Organization ID not found",
				};
			}

			const { data: fileData, error } = await supabase
				.from(SupabaseTableEnum.FILES)
				.insert([
					{
						file_name: fileName,
						bucket_name: StorageBucketsEnum.V1,
						path_name: pathName,
						job_id: job.id,
						meta_data: createFileMetadata(
							{
								name: fileName,
								type: "pdf",
							},
							templateType
						),
						organization_id: organizationId,
						job_document_id: jobDocument.id,
					},
				])
				.select();

			if (error) {
				setJobDocumentLoading(null);
				showNotification({
					message:
						"Fehler beim Erstellen des Datenbank-Eintrags. Bitte versuchen Sie es erneut.",
					type: "error",
				});
				Logger.error(error, {}, "Error creating document");
				return {
					success: false,
					error: "Fehler beim Erstellen des Datenbank-Eintrags. Bitte versuchen Sie es erneut.",
				};
			}

			if (fileData && fileData.length > 0) {
				fileId = fileData[0].id;
			}
			resultBlob = exportBlob;
			resultFileName = fileName;
		} else if (documentIsXml(templateType)) {
			if (!job.patient_id || !patientsLookup[job.patient_id]) {
				setJobDocumentLoading(null);
				setFieldProblems(job.id, "xml-patient", [NO_PATIENT_PROBLEM]);
				showNotification({
					message:
						"Bitte wählen Sie einen Patienten aus, bevor Sie das XML-Dokument exportieren.",
					type: "warning",
				});
				return {
					success: false,
					error: "Bitte wählen Sie einen Patienten aus, bevor Sie das XML-Dokument exportieren.",
				};
			}

			const fileName = getFileNameForXml({
				templateType,
				job,
				client,
				patientsLookup: patientsLookup as Record<string, LazyPatient>,
			});

			let exportError = null;
			let exportResult: ExportXmlResult | null = null;
			if (templateType === XmlTemplateType.KOSTENVORANSCHLAG_XML) {
				exportResult = await exportXmlKostenvoranschlag(
					completeJobInformation,
					documentProps.patient as PatientEntityType,
					fileName
				);
				exportError = exportResult.error;

				if (!exportError) {
					updateJobDocumentStatus(
						jobDocument.id,
						KvStatusEnum.COMPLETED,
						calculateJobTotalsLegacy({
							job,
							jobDocument,
							jobItems,
							organization,
							client,
						}).total.value,
						null
					);
				}
			} else if (templateType === XmlTemplateType.LIEFERSCHEIN_XML) {
				exportResult = await exportXmlLieferschein(
					completeJobInformation,
					documentProps.patient as PatientEntityType,
					fileName
				);
				exportError = exportResult.error;

				const shouldCompleteDocument = shouldMarkDocumentCompleted(
					jobDocument.status as JobStatusEnum
				);

				if (!exportError && shouldCompleteDocument) {
					updateJobDocumentStatus(
						jobDocument.id,
						LsStatusEnum.COMPLETED,
						calculateJobTotalsLegacy({
							job,
							jobDocument,
							jobItems,
							organization,
							client,
						}).total.value,
						null
					);
				}
			}

			if (exportError) {
				setJobDocumentLoading(null);
				// A service failure without problems says nothing about the fields.
				if (exportResult?.problems) {
					applySumexProblems(job.id, exportResult.problems);
				}

				// A missing field on another screen gets a way there; a service failure
				// gets the support chat. They are mutually exclusive: a preflight failure
				// never reached the service, so it has no diagnostic to report.
				const settingsTarget = settingsTargetFor(
					exportResult?.problems ?? [],
					client.id
				);
				const supportMessage = exportResult?.supportMessage;

				showNotification({
					message: exportError,
					description: exportResult?.errorDetail,
					type: "error",
					duration: 10000,
					action: settingsTarget
						? {
								label: settingsTarget.label,
								onClick: () => navigate(settingsTarget.path),
							}
						: supportMessage
							? {
									label: "Support kontaktieren",
									onClick: () =>
										openSupportChat(supportMessage),
								}
							: undefined,
				});
				Logger.error(
					JSON.stringify({
						completeJobInformation,
						patientsLookup: patientsLookup[job.patient_id],
						fileName,
						exportError,
					}),
					{
						category: ErrorCategoryTag.EXPORT_DOCUMENT_XML,
					},
					{ exportError }
				);
				return {
					success: false,
					error:
						exportError ||
						"Fehler beim Erstellen des Dokuments (XML Export).",
				};
			}

			applySumexProblems(job.id, []);
		}

		// wait for 1 second before refetching the job documents
		// TODO: instead call database to see when the file is there
		await new Promise((resolve) => setTimeout(resolve, 1000));
		await fetchJobDocument(jobDocument.id);
		setJobDocumentLoading(null);

		if (resultBlob && isAutoPrintEnabled(templateType as string)) {
			dispatchPrint({
				documentType: templateType as string,
				documentData: resultBlob,
				fileName: resultFileName ?? "Dokument.pdf",
			});
		}

		return {
			success: true,
			fileId,
			blob: resultBlob,
			fileName: resultFileName,
		};
	};

	const handleExportAndPrintDocument = async (
		templateType: PdfTemplateType | XmlTemplateType,
		jobItems: JobItemEntityType[],
		jobDocument: JobDocumentWithFiles,
		akontoToUse: number = 0
	): Promise<HandleExportDocumentResult> => {
		const result = await handleExportDocument(
			templateType,
			jobItems,
			jobDocument,
			akontoToUse
		);

		if (!result.success) return result;

		if (!result.blob) {
			showNotification({
				message:
					"Dokument exportiert, aber Druckauftrag konnte nicht erstellt werden",
				type: "warning",
			});
			return result;
		}

		if (!isAutoPrintEnabled(templateType as string)) {
			await dispatchPrint({
				documentType: templateType as string,
				documentData: result.blob,
				fileName: result.fileName ?? "Dokument.pdf",
			});
		}

		return result;
	};

	return {
		jobDocumentLoading,
		handleExportDocument,
		handleExportAndPrintDocument,
	};
};
